import React from "react";
import { Handle, Position, NodeProps, EdgeProps, getSmoothStepPath, EdgeLabelRenderer, BaseEdge } from "@xyflow/react";
import { getNodeIcon } from "../types";
import { getNodeColors } from "../WorkflowNodeHelpers";
import { channelDefs, Channel } from "@/types";
import IconFlag from "@/icons/IconFlag";
import IconPlus from "@/icons/IconPlus";
import IconTrash2 from "@/icons/IconTrash2";
import IconCopy from "@/icons/IconCopy";
import { FlowWorkflowNodeData } from "./xyFlowUtils";

const COLOR_TO_HEX: Record<string, string> = {
  "bg-blue-500": "#3b82f6",
  "bg-green-500": "#22c55e",
  "bg-amber-500": "#f59e0b",
  "bg-grape-500": "#7c3aed",
};

export function WorkflowFlowNode({ data, selected }: NodeProps) {
  const { wfNode, nodeText } = data as FlowWorkflowNodeData & {
    onSelect?: () => void;
    onDelete?: () => void;
    onCopy?: () => void;
    channelPerspective?: string | null;
  };
  const NodeIcon = getNodeIcon(wfNode);
  const colors = getNodeColors(wfNode);
  const headerBg = COLOR_TO_HEX[colors.bgColor] ?? "#7c3aed";
  const borderColor = selected ? headerBg : "#e2e8f0";
  const shadowClass = selected ? "shadow-lg" : "shadow-sm";

  const isTrigger = wfNode.type === "trigger";

  // Action node warnings
  const hasWarning =
    wfNode.actionType === "transferToAI" && !(wfNode.aiConfigId > 0);
  const hasAiNoIndex =
    (wfNode.actionType === "transferToAI" ||
      wfNode.actionType?.endsWith("AI")) &&
    wfNode.aiConfigId > 0 &&
    (data as any).aiHasNoIndex;

  // Trigger node warnings
  const scheduledNoExpression =
    isTrigger &&
    wfNode.trigger === "scheduled" &&
    !(wfNode as any).triggerConfig?.scheduleExpression;

  const channelPerspective = (data as any).channelPerspective as string | null | undefined;
  const triggerChannels: string[] = (wfNode as any).channels ?? [];
  const channelMismatch =
    isTrigger &&
    channelPerspective &&
    triggerChannels.length > 0 &&
    !triggerChannels.includes(channelPerspective);

  const hasTriggerWarning = scheduledNoExpression || channelMismatch;

  const { onSelect, onDelete, onCopy, isMultiSelected } = data as {
    onSelect?: () => void;
    onDelete?: () => void;
    onCopy?: () => void;
    isMultiSelected?: boolean;
  };

  const multiBorderColor = isMultiSelected ? "#6366f1" : borderColor;
  const multiBorderWidth = isMultiSelected ? 2 : (selected ? 2 : 1);

  return (
    <div
      style={{ width: 248, borderColor: multiBorderColor, borderWidth: multiBorderWidth }}
      className={`rounded-2xl overflow-hidden ${shadowClass} border bg-white cursor-pointer group ${isMultiSelected ? "ring-2 ring-indigo-300/60" : ""}`}
      onClick={onSelect}
    >
      {/* Trigger nodes have no incoming connection — hide the target handle */}
      {!isTrigger && (
        <Handle
          type="target"
          position={Position.Top}
          className="!w-3 !h-3 !bg-slate-300 !border-2 !border-white"
          style={{ top: -6 }}
        />
      )}

      {/* Header */}
      <div
        className="px-3 py-2 flex items-center gap-2"
        style={{ backgroundColor: headerBg }}
      >
        <NodeIcon className="text-white w-[18px] h-[18px] shrink-0" />
        <span className="text-white text-sm font-medium truncate flex-1">
          {wfNode.name || (data as any).nodeTitle}
        </span>
        {/* Action buttons — visible on hover */}
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
          {onCopy && (
            <button
              type="button"
              className="w-5 h-5 rounded bg-white/20 hover:bg-white/40 flex items-center justify-center"
              onClick={(e) => { e.stopPropagation(); onCopy(); }}
              title="Copy node"
            >
              <IconCopy className="w-3 h-3 text-white" />
            </button>
          )}
          {onDelete && (
            <button
              type="button"
              className="w-5 h-5 rounded bg-white/20 hover:bg-white/40 flex items-center justify-center"
              onClick={(e) => { e.stopPropagation(); onDelete(); }}
              title="Delete node"
            >
              <IconTrash2 className="w-3 h-3 text-white" />
            </button>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="px-3 py-2 min-h-[36px] flex items-center justify-between gap-2">
        <span
          className={`text-xs leading-snug line-clamp-2 ${
            nodeText ? "text-slate-700" : "text-slate-300 italic"
          }`}
        >
          {nodeText || "Click to configure"}
        </span>
        {/* Channel badge for restricted-channel triggers */}
        {isTrigger && triggerChannels.length > 0 && (
          <span className="shrink-0 px-1.5 py-0.5 bg-blue-50 border border-blue-200 rounded text-[10px] text-blue-600 font-medium whitespace-nowrap">
            {triggerChannels.map(ch => channelDefs[ch as Channel] ?? ch).join(", ")}
          </span>
        )}
      </div>

      {/* Action node warning bars */}
      {hasWarning && (
        <div className="px-3 py-1 bg-red-50 border-t border-red-200 text-xs text-red-600 flex items-center gap-1">
          ⚠ No AI Configuration selected
        </div>
      )}
      {hasAiNoIndex && !hasWarning && (
        <div className="px-3 py-1 bg-orange-50 border-t border-orange-200 text-xs text-orange-700 flex items-center gap-1">
          ⚠ No knowledge index — AI has no content
        </div>
      )}

      {/* Trigger warning bars */}
      {scheduledNoExpression && (
        <div className="px-3 py-1 bg-amber-50 border-t border-amber-200 text-xs text-amber-700 flex items-center gap-1">
          ⚠ No schedule set — workflow won't run
        </div>
      )}
      {channelMismatch && (
        <div className="px-3 py-1 bg-amber-50 border-t border-amber-200 text-xs text-amber-700 flex items-center gap-1">
          ⚠ Doesn't fire on {channelPerspective} — check channel filter
        </div>
      )}
      {isTrigger && !hasTriggerWarning && triggerChannels.length === 0 && (
        <div className="px-3 py-1 bg-blue-50 border-t border-blue-100 text-[10px] text-blue-500 flex items-center gap-1">
          Fires on all channels · click to restrict
        </div>
      )}

      <Handle
        type="source"
        position={Position.Bottom}
        className="!w-3 !h-3 !bg-slate-300 !border-2 !border-white"
        style={{ bottom: -6 }}
      />
    </div>
  );
}

export function WorkflowEndNode() {
  return (
    <div className="flex items-center justify-center">
      <Handle
        type="target"
        position={Position.Top}
        className="!w-2 !h-2 !bg-slate-300 !border-0"
        style={{ top: -4 }}
      />
      <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center border border-slate-300">
        <IconFlag className="w-4 h-4 text-slate-500" />
      </div>
    </div>
  );
}

export function WorkflowEdge({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  label,
  data,
  markerEnd,
}: EdgeProps) {
  const [edgePath, labelX, labelY] = getSmoothStepPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
    borderRadius: 8,
  });

  const midX = (sourceX + targetX) / 2;
  const midY = (sourceY + targetY) / 2;

  const onAddBetween = (data as { onAddBetween?: (x: number, y: number) => void })
    ?.onAddBetween;

  return (
    <>
      <BaseEdge id={id} path={edgePath} markerEnd={markerEnd} style={{ stroke: "#cbd5e1", strokeWidth: 2 }} />
      <EdgeLabelRenderer>
        {label && (
          <div
            style={{
              position: "absolute",
              transform: `translate(-50%,-50%) translate(${labelX}px,${labelY}px)`,
              pointerEvents: "none",
            }}
            className="px-2 py-0.5 bg-white border border-slate-200 rounded-full text-xs text-slate-600 shadow-sm font-medium whitespace-nowrap"
          >
            {label as string}
          </div>
        )}
        {onAddBetween && (
          <div
            style={{
              position: "absolute",
              transform: `translate(-50%,-50%) translate(${midX}px,${midY}px)`,
              pointerEvents: "all",
            }}
            className="w-6 h-6 rounded-full bg-white border-2 border-slate-300 flex items-center justify-center cursor-pointer hover:border-blue-400 hover:bg-blue-50 text-slate-400 hover:text-blue-500 shadow-sm transition-all opacity-30 hover:opacity-100 nodrag nopan"
            onClick={(e) => {
              e.stopPropagation();
              onAddBetween(e.clientX, e.clientY);
            }}
            title="Add node here"
          >
            <IconPlus className="w-3 h-3" />
          </div>
        )}
      </EdgeLabelRenderer>
    </>
  );
}
